import { EmptyState } from '@agentic-flow-studio/shared/components/empty-state';
import { formatOffsetTime } from '@agentic-flow-studio/shared/lib/job-plan-time';
import { trpc } from '@agentic-flow-studio/shared/lib/trpc';
import { Badge } from '@agentic-flow-studio/shared/ui/badge';
import { Button } from '@agentic-flow-studio/shared/ui/button';
import { Card } from '@agentic-flow-studio/shared/ui/card';
import { Clock, Edit2, MapPin, Plus, Users, Wrench } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

export default function OperatorsPage() {
  const { orgId, workspaceId } = useParams<{ orgId: string; workspaceId: string }>();
  const basePath = `/org/${orgId}/workspace/${workspaceId}/operators`;

  const { data: operators = [], isLoading } = trpc.jobPlan.operators.list.useQuery();
  const { data: members = [] } = trpc.workspace.listMembers.useQuery();

  const existingUserIds = new Set(operators.map((op) => op.userId));
  const availableMembers = members.filter((m) => !existingUserIds.has(m.id));

  return (
    <div className="flex flex-col h-full overflow-hidden bg-background">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-border/40 bg-card">
        <div className="flex items-center gap-3">
          <h1 className="text-lg font-bold tracking-tight">Operators</h1>
          <Badge variant="outline" className="text-xs">
            {operators.filter((o) => o.isActive).length} Active / {operators.length} Total
          </Badge>
        </div>

        <Button size="sm" asChild disabled={availableMembers.length === 0} className="gap-1.5">
          <Link to={`${basePath}/new`}>
            <Plus className="h-4 w-4" />
            <span>Add Operator</span>
          </Link>
        </Button>
      </div>

      {/* Operators List */}
      <div className="flex-1 overflow-y-auto p-4">
        {isLoading ? (
          <div className="text-center py-12 text-muted-foreground text-sm">
            Loading operators...
          </div>
        ) : operators.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No operators configured"
            description="Add team members as field operators to assign jobs, capabilities, and optimize routes."
            action={
              <Button size="sm" asChild disabled={availableMembers.length === 0}>
                <Link to={`${basePath}/new`}>
                  <Plus className="h-3.5 w-3.5 mr-1" />
                  <span>Add Operator</span>
                </Link>
              </Button>
            }
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {operators.map((op) => (
              <Card
                key={op.id}
                className={`p-4 flex flex-col justify-between gap-3 transition-shadow hover:shadow-md ${
                  !op.isActive ? 'opacity-60 bg-muted/30' : ''
                }`}
              >
                <div className="flex flex-col gap-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm">
                        {(op.user?.fullName || op.user?.email || 'U')[0].toUpperCase()}
                      </div>
                      <div className="flex flex-col">
                        <span className="font-semibold text-sm">
                          {op.user?.fullName || op.user?.email}
                        </span>
                        <span className="text-[11px] text-muted-foreground">{op.user?.email}</span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5">
                      <Badge
                        variant="outline"
                        className={
                          op.isActive
                            ? 'text-[10px] text-emerald-600 bg-emerald-500/10 border-emerald-500/20'
                            : 'text-[10px] text-muted-foreground bg-muted border-border'
                        }
                      >
                        {op.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="icon"
                        asChild
                        className="h-7 w-7 text-muted-foreground hover:text-foreground"
                        title="Edit Operator & Skills"
                      >
                        <Link to={`${basePath}/${op.id}/edit`}>
                          <Edit2 className="h-3.5 w-3.5" />
                        </Link>
                      </Button>
                    </div>
                  </div>

                  {/* Shift & Location Details */}
                  <div className="flex flex-col gap-1.5 text-xs text-muted-foreground pt-1 border-t border-border/40">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <Clock className="h-3.5 w-3.5 text-muted-foreground/70" />
                        <span>
                          {formatOffsetTime(op.shiftStartOffsetSeconds)} –{' '}
                          {formatOffsetTime(op.shiftEndOffsetSeconds)}
                        </span>
                      </div>
                      <span>{Math.round(op.breakDurationSeconds / 60)}m break</span>
                    </div>

                    {op.startLocation && (
                      <div className="flex items-center gap-1.5 truncate text-[11.5px]">
                        <MapPin className="h-3.5 w-3.5 text-muted-foreground/70 shrink-0" />
                        <span className="truncate">
                          {op.startLocation.label}
                          {op.endLocation == null ? (
                            <span className="text-primary font-medium ml-1">
                              ➔ 🏁 Finish at last stop
                            </span>
                          ) : op.endLocation.id === op.startLocation.id ? (
                            <span className="text-muted-foreground/80 ml-1">(Round Trip)</span>
                          ) : (
                            <span className="text-muted-foreground/90 ml-1">
                              ➔ 🏢 {op.endLocation.label}
                            </span>
                          )}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Skills Badges */}
                  <div className="flex flex-wrap items-center gap-1 pt-1 border-t border-border/40">
                    <Wrench className="h-3 w-3 text-muted-foreground/60 shrink-0" />
                    {!op.skills || op.skills.length === 0 ? (
                      <span className="text-[11px] text-muted-foreground italic">
                        No skills assigned
                      </span>
                    ) : (
                      op.skills.map((skill) => (
                        <Badge
                          key={skill.id}
                          variant="outline"
                          style={{
                            backgroundColor: skill.color ? `${skill.color}15` : undefined,
                            color: skill.color || undefined,
                          }}
                          className="text-[10px] px-1.5 py-0"
                        >
                          {skill.name}
                        </Badge>
                      ))
                    )}
                  </div>
                </div>

                <div className="pt-2 border-t border-border/40 flex justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    asChild
                    className="h-7 text-xs gap-1.5 w-full"
                  >
                    <Link to={`${basePath}/${op.id}/edit`}>
                      <Edit2 className="h-3 w-3" />
                      <span>Edit Profile & Skills</span>
                    </Link>
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
